import { GPC_METHODOLOGY_LOOKUP_MAP } from "./methodology-lookup";
/**
 * GPC AFOLU sector (Agriculture, Forestry and Other Land Use) — activity-based
 * emission factors. AFOLU is one of the GPC "calculated" sectors: emissions are
 * `activity × factor` over a 2D (category × subsector) grid. The shared logic
 * lives in calculated-sector.ts; this module is just AFOLU's data.
 */
export const AFOLU_GWP_AR6 = {
    CO2: 1,
    N2O: 273,
    CH4: 27,
};
/**
 * The greenhouse gases the AFOLU factor table breaks down into — the per-gas
 * editing dimension.
 */
export const AFOLU_FACTOR_GASES = ["CO2", "N2O", "CH4"];
export const AFOLU_GAS_GWP = {
    CO2: AFOLU_GWP_AR6.CO2,
    N2O: AFOLU_GWP_AR6.N2O,
    CH4: AFOLU_GWP_AR6.CH4,
};
/** Short gas labels for the per-gas breakdown rows in the UI. */
const AFOLU_GAS_LABEL = {
    CO2: "CO₂",
    N2O: "N₂O",
    CH4: "CH₄",
};
const AFOLU_ACTIVITY_UNIT_LABEL = {
    head: "Tiere",
    ha: "ha",
    t: "t",
};
const AFOLU_FACTOR_UNIT_LABEL = {
    head: "tCO₂äq/Tier",
    ha: "tCO₂äq/ha",
    t: "tCO₂äq/t",
};
const { AFOLU } = GPC_METHODOLOGY_LOOKUP_MAP.sectors;
// Per-category activity unit: animals for livestock, hectares for land use,
// tonnes for the other agricultural sources. Placeholder basis.
const AFOLU_ROW_UNIT_BY_KIND = {
    CATTLE: "head",
    PIGS: "head",
    POULTRY: "head",
    SHEEP_GOATS: "head",
    HORSES: "head",
    FOREST: "ha",
    CROPLAND: "ha",
    GRASSLAND: "ha",
    WETLANDS: "ha",
    SETTLEMENTS: "ha",
    LIMING: "t",
    UREA: "t",
    CROP_RESIDUES: "t",
};
/**
 * 2D default factor table, per gas in **kg CO₂e per activity unit** (a value may
 * be negative — land-use carbon removals). A present entry means the cell is
 * applicable. Livestock categories belong to Tierhaltung; land-use categories to
 * Landnutzung; other agricultural sources to Sonstige.
 *
 * PLACEHOLDER VALUES — see the per-cell comments. Keyed `${categoryKind}|${subsectorKind}`.
 */
const AFOLU_DEFAULT_FACTORS = new Map([
    // --- Tierhaltung: IPCC 2006 Vol.4 Ch.10 Tier-1, cool/Western-Europe, per animal/yr ---
    // CH₄ = (enteric + manure) kg CH₄/head/yr × 27. N₂O = indicative Annahme (Tier-2 needed).
    ["CATTLE|LIVESTOCK", { CH4: 3834, N2O: 250 }], // dairy: (117 enteric + 25 manure) × 27
    ["PIGS|LIVESTOCK", { CH4: 230, N2O: 110 }], // (1.5 enteric + 7 manure) × 27
    ["POULTRY|LIVESTOCK", { CH4: 3, N2O: 14 }], // enteric ~0; manure ~0.1 kg × 27
    ["SHEEP_GOATS|LIVESTOCK", { CH4: 221, N2O: 40 }], // sheep (8 enteric + 0.19 manure) × 27
    ["HORSES|LIVESTOCK", { CH4: 528, N2O: 60 }], // (18 enteric + 1.56 manure) × 27
    // --- Landnutzung / LULUCF, per ha/yr ---
    // Managed-forest living-biomass sink — indicative German default (Thünen /
    // Bundeswaldinventur ~ −4 t CO₂/ha·a). Highly variable and recently near-neutral
    // (drought / bark beetle); override with local stock-change / BWI data. (Soil
    // stock change for forest-remaining-forest is the Tier-1 ≈ 0 part, folded in.)
    ["FOREST|LAND_USE", { CO2: -4000 }],
    // Cropland / grassland / settlements: IPCC Tier-1 stock change for land REMAINING
    // in use ≈ 0; Germany's real (peat-driven) land-use emissions need site-specific
    // data → these stay 0 placeholders to be overridden per inventory.
    ["CROPLAND|LAND_USE", { CO2: 0 }],
    ["GRASSLAND|LAND_USE", { CO2: 0 }],
    // Rewetted organic wetlands — Thünen/Tiemeyer 2020: CO₂ sink −0.4 t C/ha (×44/12 →
    // −1467 kg CO₂/ha), CH₄ source 279 kg/ha (×27 → 7533 kg CO₂e/ha). Net positive.
    ["WETLANDS|LAND_USE", { CO2: -1467, CH4: 7533 }],
    ["SETTLEMENTS|LAND_USE", { CO2: 0 }],
    // --- Sonstige: IPCC 2006 Vol.4 Ch.11, per tonne applied ---
    ["LIMING|OTHER", { CO2: 440 }], // limestone CaCO₃: 0.12 t C/t × 44/12 → 440 kg CO₂/t
    ["UREA|OTHER", { CO2: 733 }], // urea: 0.20 t C/t × 44/12 → 733 kg CO₂/t
    // Crop residues: EF1 0.01 kg N₂O-N/kg N × ~1% residue N × 44/28 × 273 ≈ 43 kg CO₂e/t.
    ["CROP_RESIDUES|OTHER", { N2O: 43 }],
]);
// Per-source provenance. Most factors are now traceable to a published table; the
// few that aren't (livestock manure N₂O, general land stock-change) are labelled
// honestly so the UI does not over-claim a source.
const PROV_IPCC_CH10 = {
    dataSource: "IPCC 2006 GL Vol. 4 Kap. 10 (Tier 1, Westeuropa/kühl)",
    referenceYear: 2006,
    quality: "M",
};
const PROV_IPCC_CH11 = {
    dataSource: "IPCC 2006 GL Vol. 4 Kap. 11",
    referenceYear: 2006,
    quality: "M",
};
const PROV_IPCC_EF1 = {
    dataSource: "IPCC 2006 GL Vol. 4 Kap. 11 (EF1, Annahme ~1 % Ernterückstand-N)",
    referenceYear: 2006,
    quality: "L",
};
const PROV_IPCC_TIER1_LAND = {
    dataSource: "IPCC 2006 GL Vol. 4 (Tier 1: Bestandsänderung ≈ 0; standortspezifische Daten nötig)",
    referenceYear: 2006,
    quality: "L",
};
const PROV_THUENEN = {
    dataSource: "Thünen / Tiemeyer et al. 2020 (organische Böden)",
    referenceYear: 2020,
    quality: "H",
};
// Forest living-biomass sink — indicative German default, jährlich stark schwankend
// (Dürre/Borkenkäfer), daher quality L und ausdrücklich zum Überschreiben gedacht.
const PROV_FOREST_SINK = {
    dataSource: "Annahme (Thünen/BWI, lebende Biomasse)",
    referenceYear: null,
    quality: "L",
};
// Livestock manure N₂O has no robust per-head Tier-1 value here — indicative only.
const PROV_ANNAHME = { dataSource: "Annahme", referenceYear: null, quality: "L" };
const AFOLU_DEFAULT_PROVENANCE = new Map([
    // Livestock: CH₄ from IPCC Ch.10; manure N₂O indicative.
    ["LIVESTOCK|CH4", PROV_IPCC_CH10],
    ["LIVESTOCK|N2O", PROV_ANNAHME],
    // Land use: general stock change ≈ 0 (Tier-1); forest sink + wetlands are overrides.
    ["LAND_USE|CO2", PROV_IPCC_TIER1_LAND],
    ["FOREST|LAND_USE|CO2", PROV_FOREST_SINK],
    ["WETLANDS|LAND_USE|CO2", PROV_THUENEN],
    ["WETLANDS|LAND_USE|CH4", PROV_THUENEN],
    // Aggregate sources: liming/urea CO₂ from IPCC Ch.11; crop-residue N₂O from EF1.
    ["OTHER|CO2", PROV_IPCC_CH11],
    ["OTHER|N2O", PROV_IPCC_EF1],
]);
/** The AFOLU calculated-sector config — drives recompute and the frontend grids. */
export const AFOLU_FACTORS = {
    key: "AFOLU",
    gases: AFOLU_FACTOR_GASES,
    fgasSpecies: [],
    gasGwp: AFOLU_GAS_GWP,
    gasLabel: AFOLU_GAS_LABEL,
    scopes: [1],
    allowsNegativeFactors: true,
    activityUnitLabel: AFOLU_ACTIVITY_UNIT_LABEL,
    factorUnitLabel: AFOLU_FACTOR_UNIT_LABEL,
    rowUnitByKind: AFOLU_ROW_UNIT_BY_KIND,
    defaultFactors: AFOLU_DEFAULT_FACTORS,
    defaultProvenance: AFOLU_DEFAULT_PROVENANCE,
    categoryKindById: new Map(Object.entries(AFOLU.energySources).map(([kind, source]) => [source.id, kind])),
    subsectorKindById: new Map(Object.entries(AFOLU.subsectors).map(([kind, subsector]) => [subsector.id, kind])),
};
